import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { type SupportedCurrency } from '../lib/currency';
import { useCurrency } from './CurrencyContext';

export interface ProductPrice {
  id: string;
  name: string;
  description: string;
  price_brl: number;
  price_usd: number;
  credits: number | null;
  category: 'subscription' | 'questions' | 'synastry' | 'astromatch' | 'gift_card';
  stripe_price_id_brl?: string;
  stripe_price_id_usd?: string;
  active: boolean;
}

interface PricingContextValue {
  products: ProductPrice[];
  loading: boolean;
  fetchError: boolean;
  getPrice: (productId: string) => number;
  formatProductPrice: (productId: string) => string;
  isFree: (productId: string) => boolean;
  getProduct: (productId: string) => ProductPrice | undefined;
}

const PricingContext = createContext<PricingContextValue>({
  products: [],
  loading: true,
  fetchError: false,
  getPrice: () => 0,
  formatProductPrice: () => '',
  isFree: () => false,
  getProduct: () => undefined,
});

export function PricingProvider({ children }: { children: ReactNode }) {
  const { currency, format } = useCurrency();
  const [products, setProducts] = useState<ProductPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    fetchProducts();
    // Subscribe to realtime changes
    const channel = supabase
      .channel('product_prices_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'product_prices' }, () => fetchProducts())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  async function fetchProducts() {
    const { data, error } = await supabase
      .from('product_prices')
      .select('*')
      .eq('active', true)
      .order('price_brl');

    if (error) {
      setFetchError(true);
      setLoading(false);
      return;
    }

    setFetchError(false);
    if (data) setProducts(data);
    setLoading(false);
  }

  function getPrice(productId: string): number {
    const p = products.find(x => x.id === productId);
    if (!p) return 0;
    return currency === 'BRL' ? p.price_brl : p.price_usd;
  }

  function formatProductPrice(productId: string): string {
    return format(getPrice(productId));
  }

  function isFree(productId: string): boolean {
    return getPrice(productId) === 0;
  }

  function getProduct(productId: string): ProductPrice | undefined {
    return products.find(x => x.id === productId);
  }

  return (
    <PricingContext.Provider value={{ products, loading, fetchError, getPrice, formatProductPrice, isFree, getProduct }}>
      {children}
    </PricingContext.Provider>
  );
}

export function usePricing(): PricingContextValue {
  return useContext(PricingContext);
}

export type { SupportedCurrency };
